import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  generatePath, matchPath, Outlet, useLocation, useParams,
} from 'react-router-dom';
import BreadcrumbsNav from 'components/BreadcrumbsNav/BreadcrumbsNav';
import NavSidebar from 'components/NavSidebar/NavSidebar';
import Title from 'components/Title/Title';
import AppRoutes, { type TAppRoutes } from 'constants/AppRoutes';
import { dataSetsNav } from 'constants/navigationLists';
import useBreadcrumbs from 'hooks/useBreadcrumbs';

import type { ReactNode } from 'react';
import type IDataSetsOutletContext from './interfaces/IDataSetsOutletContext';

import styles from './scss/DataSetsLayout.module.scss';

function DataSetsLayout() {
  const { t } = useTranslation();
  const { recordId = '' } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const [titleAction, setTitleAction] = useState<ReactNode | null>(null);

  const { breadcrumbs } = useBreadcrumbs({ mathCrumbs: [] });

  const navList = dataSetsNav();

  const isNavMenuVisible = [
    AppRoutes.profitCentres,
    AppRoutes.balanceSheetAssets,
    AppRoutes.suppliers,
    AppRoutes.customers,
    AppRoutes.products,
  ].some((path) => matchPath(path, pathname));

  const pageTitle: { [key in TAppRoutes]?: string } = {
    [AppRoutes.profitCentresCreate]: t('profitCentres.createProfitCentre.title'),
    [generatePath(AppRoutes.profitCentresEdit, { recordId })]: t('profitCentres.createProfitCentre.editTitle'),
    [AppRoutes.balanceSheetAssetsCreate]: t('ownOperations.addNewBalanceSheetAsset'),
    [generatePath(AppRoutes.balanceSheetAssetsEdit, { recordId })]: t('ownOperations.editBalanceSheetAsset'),
    [AppRoutes.supplierCreate]: t('suppliers.addNewSupplier'),
    [generatePath(AppRoutes.supplierEdit, { recordId })]: t('suppliers.editSupplier'),
    [AppRoutes.productsCreate]: t('products.addNewInputProduct'),
    [generatePath(AppRoutes.productsEdit, { recordId })]: t('products.editInputProduct'),
    [AppRoutes.customerCreate]: t('customers.addNewCustomer'),
    [generatePath(AppRoutes.customerEdit, { recordId })]: t('customers.editCustomer'),
  };
  const pageTitleText = pageTitle[pathname as TAppRoutes] || t('pillarsAndModules.settings/data-sets-management');
  const outletContext: IDataSetsOutletContext = { setTitleAction };

  return (
    <div className={styles.root}>
      <BreadcrumbsNav breadcrumbs={breadcrumbs} />
      <div className={styles.titleRow}>
        <Title
          text={pageTitleText}
          className={styles.title}
        />
        {titleAction ? (
          <div className={styles.titleAction}>
            {titleAction}
          </div>
        ) : null}
      </div>
      <div className={styles.wrapper}>
        {isNavMenuVisible ? (
          <div className={styles.sidebar}>
            <NavSidebar
              navList={navList}
              activeClassName={styles.active}
            />
          </div>
        ) : null}
        <div className={styles.content}>
          <Outlet context={outletContext} />
        </div>
      </div>
    </div>
  );
}

export default DataSetsLayout;
